import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';
import useLocation from './useLocation';

export interface OutputParam {
    key: string;
    value: string | null;
}

interface InputParam {
    key: string;
    value: string | null | undefined;
}

/**
 * Hook that returns a list with all query params in the order of their occurrence and a function to add/change/remove query params.
 * Using `undefined` as a new query param value deletes that param.
 * The function to update query params only touches params that are listed in its argument. New or changed params move to the pack of the search params.
 *
 * @param action Can be `'push'` or `'replace'` depending on whether a new history state should be pushed or the current one should be replaced.
 */
export default function useSearchParams(
    action: 'push' | 'replace' = 'push'
): [OutputParam[], (v: InputParam[]) => void] {
    const { search } = useLocation();
    const { push: pushState, replace: replaceState } = useHistory();

    const updateLocationDescriptor = action === 'push' ? pushState : replaceState;
    const values = useMemo(() => getAllParams(search), [search]);

    const setValues = useCallback(
        (change: InputParam[]) => {
            const keys = change.map(({ key }) => key);
            const params = getAllParams(search).filter(({ key }) => !keys.includes(key));

            updateLocationDescriptor({ search: stringifyParams([...params, ...change]) });
        },
        [search, updateLocationDescriptor]
    );

    return [values, setValues];
}

// inspired by https://github.com/sindresorhus/query-string/blob/master/index.js#L211-L248
function stringifyParams(input: { key: string; value: string | null | undefined }[]): string {
    return input
        .map(({ key, value }) => {
            if (value === undefined) {
                return '';
            }

            if (value === null) {
                return encodeURIComponent(key);
            }

            return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        })
        .filter(x => x.length > 0)
        .join('&');
}

// inspired by https://github.com/sindresorhus/query-string/blob/master/index.js#L164-L206
function getAllParams(input: string): { key: string; value: string | null }[] {
    const ret: { key: string; value: string | null }[] = [];

    input = input.trim().replace(/^[?#&]/, '');

    for (const param of input.split('&')) {
        const [key, orgValue]: [string, string | undefined] = splitOnFirst(param.replace(/\+/g, ' '), '=');

        const value = orgValue === undefined ? null : decodeURIComponent(orgValue);

        ret.push({ key, value });
    }

    return ret;
}

// source: https://github.com/sindresorhus/split-on-first/blob/master/index.js
function splitOnFirst(string: string, separator: string): [string, string | undefined] {
    if (separator === '') {
        return [string, undefined];
    }

    const separatorIndex = string.indexOf(separator);

    if (separatorIndex === -1) {
        return [string, undefined];
    }

    return [string.slice(0, separatorIndex), string.slice(separatorIndex + separator.length)];
}
